/**
 * useSubtaskOperations — CRUD operations for subtasks within todos.
 */
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { supabase } from "@/integrations/supabase/client";
import { logger } from '@/utils/logger';

export const useSubtaskOperations = (invalidateGoals: () => Promise<void>) => {
  const { handleError, handleSuccess } = useErrorHandler();

  const addSubtask = async ({ goalTitle, todoId, subtaskText }: { goalTitle: string, todoId: string, subtaskText: string }) => {
    try {
      logger.debug("Adding subtask:", { goalTitle, todoId, subtaskText });
      
      const { data, error } = await supabase
        .from('subtasks')
        .insert([{
          todo_id: todoId,
          text: subtaskText || "New subtask",
          completed: false
        }])
        .select()
        .single();

      if (error) throw error;
      await invalidateGoals();
      
      handleSuccess("Subtask has been added successfully.", { title: "Subtask added" });
      
      return Promise.resolve(data);
    } catch (error: any) {
      logger.error("Error adding subtask:", error);
      handleError(error, { title: "Error adding subtask" });
      return Promise.reject(error);
    }
  };

  const deleteSubtask = async ({ goalTitle, todoId, subtaskId }: { goalTitle: string, todoId: string, subtaskId: string }) => {
    try {
      const { error } = await supabase
        .from('subtasks')
        .delete()
        .eq('id', subtaskId);

      if (error) throw error;
      await invalidateGoals();
      
      handleSuccess("Subtask has been deleted successfully.", { title: "Subtask deleted" });
      
      return Promise.resolve();
    } catch (error: any) {
      handleError(error, { title: "Error deleting subtask" });
      return Promise.reject(error);
    }
  };

  const toggleSubtask = async ({ goalTitle, todoId, subtaskId }: { goalTitle: string, todoId: string, subtaskId: string }) => {
    try {
      const { data: subtask } = await supabase
        .from('subtasks')
        .select('completed')
        .eq('id', subtaskId)
        .single();

      const { error } = await supabase
        .from('subtasks')
        .update({ completed: !subtask?.completed })
        .eq('id', subtaskId);

      if (error) throw error;
      await invalidateGoals();
      
      return Promise.resolve();
    } catch (error: any) {
      handleError(error, { title: "Error toggling subtask" });
      return Promise.reject(error);
    }
  };

  const updateSubtask = async ({ goalTitle, todoId, subtaskId, newText }: 
    { goalTitle: string; todoId: string; subtaskId: string; newText: string }) => {
    try {
      logger.debug(`[useSubtaskOperations] Updating subtask ${subtaskId} with new text: "${newText}"`);
      
      const { error } = await supabase
        .from('subtasks')
        .update({ text: newText })
        .eq('id', subtaskId);

      if (error) throw error;
      await invalidateGoals();
      
      handleSuccess("Subtask text has been updated successfully.", { title: "Subtask updated" });
      
      return Promise.resolve();
    } catch (error: any) {
      logger.error("[useSubtaskOperations] Error updating subtask:", error);
      handleError(error, { title: "Error updating subtask" });
      return Promise.reject(error);
    }
  };

  return {
    addSubtask,
    deleteSubtask,
    toggleSubtask,
    updateSubtask
  };
};
